import axios from "axios";
import type { InspectionStatus } from "../../../store/oasis/yardInspectionsStore";

// ─── Salud del controlador (#498) ─────────────────────────────────────────────
//
// La forma exacta que arma `computeYardHealth` (yardHealthService.js) y que
// `GET /truck-yard/yards` embebe en cada patio de la página. No se pide por
// separado: viene en el mismo render en que la tarjeta ya pinta el nombre, y
// ese fue el motivo explícito de embeberla (truckYardController.js:229-234) —
// un segundo request hace que la tarjeta aparezca primero con el punto viejo y
// se corrija después, que es justo el estado que #499 viene a dejar de mostrar.

/** Los siete estados del eje de enlace. Fuente: LINK_STATES en yardHealthService.js. */
export type YardLinkState =
  | "sin_controlador"
  | "en_vivo"
  | "degradado"
  | "intermitente"
  | "sin_conexion"
  | "caido"
  | "desconocido";

/** Tramos del eje de uso. Mismos nombres que `deviceTier` en accessControlSection. */
export type YardUsageTier =
  | "online_idle"
  | "active"
  | "stale_1h"
  | "stale_24h"
  | "stale_48h";

export interface YardHealth {
  link: YardLinkState;
  usage: YardUsageTier | null;
  doors_total: number;
  doors_monitored: number;
  doors_down: number;
  doors_unknown: number;
  outage_started_at: string | null;
  outage_duration_ms: number | null;
  last_event_at: string | null;
  last_event_age_ms: number | null;
  outages_24h: number;
  last_recovery_at: string | null;
  computed_at: string;
  // ── Causa del corte (5-oct-2026) ──
  // Opcionales a propósito: un backend anterior a este contrato no los manda, y
  // `undefined` se lee igual que `null` ("no se sabe"). Ver `yardDownCause`.
  down_cause?: YardDownCause | null;
  /** Último latido de la PC del sitio, el más reciente entre las puertas monitoreadas. */
  pc_last_seen_at?: string | null;
  pc_last_seen_age_ms?: number | null;
  /** Texto crudo del SDK, ya truncado por el backend. Detalle, nunca etiqueta. */
  controller_last_error?: string | null;
}

/**
 * Quién es el que no responde cuando hay puertas caídas.
 *
 *  - `pc`: no llegan latidos — la PC del sitio (el relay) está apagada,
 *    suspendida o sin internet.
 *  - `controladora`: la PC sí late, pero no logra hablar con la controladora.
 *  - `mixto`: hay puertas caídas por las dos causas.
 */
export type YardDownCause = "pc" | "controladora" | "mixto";

const DOWN_CAUSES: readonly YardDownCause[] = ["pc", "controladora", "mixto"];

/** Los tres estados de enlace en los que hay al menos una puerta caída. */
export function yardLinkHasOutage(link: YardLinkState): boolean {
  return link === "sin_conexion" || link === "caido" || link === "degradado";
}

/**
 * Cómo se pinta cada estado de enlace.
 *
 * Ninguno de los siete es verde salvo `en_vivo`, y eso es el punto entero de la
 * issue: el punto de color de la tarjeta era `yard.status === "active"` —un
 * campo que edita un humano en el formulario de alta— y siguió en verde durante
 * los seis días de corte de #490 y durante el 55,8% de indisponibilidad medido
 * entre el 19-ago y el 3-sep-2026.
 *
 * Los dos casos que el criterio de aceptación nombra por separado:
 *
 *  - `sin_controlador`: el patio no tiene ni una puerta registrada. Gris y
 *    dicho con todas las letras. Hoy es el caso de 6 de los 7 patios de
 *    producción (medido el 4-sep-2026: 7 documentos en `truck_yards`, 1 solo
 *    `hikvisionDevices`), así que es el estado MÁS común de esta pantalla, no
 *    un borde.
 *  - `desconocido`: hay puertas, pero ninguna late — el caso de un controlador
 *    en modo `direct`, cuyo `status` es un toggle manual que además nace en
 *    "online". Se pinta ámbar y NO verde: `hasHeartbeat` decide por el hecho
 *    (¿llegó algún latido?) y no por el modo declarado, justamente porque
 *    `connection_mode` cae por defecto en "hikgateway" en los dos lados
 *    (hikvisionAccessController.js:361 y accessControlSection/page.tsx:71).
 *
 * Las clases se escriben completas, sin armar el hue en runtime: Tailwind
 * escanea el texto del fuente y un `bg-${hue}-500/15` no genera CSS. Es la
 * misma advertencia que ya lleva INSPECTION_STATUS_PALETTE.
 *
 * Los hues se solapan con los de las píldoras de inspección (naranja acá es
 * "sin conexión" y allá "sin salida") y eso es a propósito: en las dos escalas
 * el naranja significa "esto necesita a alguien" y el rojo "esto ya dolió". Lo
 * que no se puede es que dos cosas del MISMO eje compartan hue, y por eso las
 * dos tiras van en renglones distintos y cada píldora lleva su etiqueta y su
 * ícono; el color ordena, no identifica.
 */
const LINK_PALETTE: Record<
  YardLinkState,
  { dot: string; chip: string; icon: string }
> = {
  en_vivo: {
    dot: "bg-green-500",
    chip: "bg-green-500/15 text-green-400 border border-green-500/30",
    icon: "ri-wifi-line",
  },
  // Ámbar y no rojo: algunas puertas responden. La causa y la reparación son
  // distintas a las de un patio entero mudo (esa es internet, energía o la PC
  // del relay; ésta es esa puerta), y la tarjeta lo dice con "N de M".
  degradado: {
    dot: "bg-amber-500",
    chip: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
    icon: "ri-signal-wifi-error-line",
  },
  intermitente: {
    dot: "bg-yellow-500",
    chip: "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30",
    icon: "ri-pulse-line",
  },
  // Naranja: el corte todavía puede ser un bache. Rojo está reservado para
  // `caido`, que es "el corte ya pasó el umbral de alerta y alguien recibió el
  // correo"; pintarlos igual borra la única diferencia que le importa al
  // operador, que es si tiene que levantar el teléfono ahora.
  sin_conexion: {
    dot: "bg-orange-500",
    chip: "bg-orange-500/15 text-orange-400 border border-orange-500/30",
    icon: "ri-signal-wifi-off-line",
  },
  caido: {
    dot: "bg-red-500",
    chip: "bg-red-500/15 text-red-400 border border-red-500/30",
    icon: "ri-alarm-warning-line",
  },
  sin_controlador: {
    dot: "bg-gray-500",
    chip: "bg-gray-500/15 text-gray-400 border border-gray-500/30",
    icon: "ri-device-line",
  },
  desconocido: {
    dot: "bg-amber-500",
    chip: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
    icon: "ri-question-line",
  },
};

export function yardLinkClasses(link: YardLinkState) {
  return LINK_PALETTE[link];
}

/**
 * Estado de enlace de un patio cuando el listado no trajo `health`.
 *
 * Pasa de verdad: el bloque que la calcula está dentro de un try/catch para que
 * una falla al leer `hikvisionDevices` no tumbe la pantalla entera
 * (truckYardController.js:245-249), y en ese caso el patio llega con
 * `health: null`. `desconocido` es la respuesta honesta; el verde de antes era
 * la mentira.
 */
export function yardLinkState(health: YardHealth | null | undefined): YardLinkState {
  const link = health?.link;
  // Un estado que este cliente no conoce cae en `desconocido` en vez de
  // reventar la tarjeta: el vocabulario lo define el backend (LINK_STATES) y
  // este archivo es una copia del contrato, no su fuente. Un despliegue que
  // agregue un octavo estado tiene que degradar, no romper la pantalla — es la
  // misma razón por la que el listado calcula la salud dentro de un try/catch.
  return link && link in LINK_PALETTE ? link : "desconocido";
}

/**
 * La causa del corte, normalizada: `pc`, `controladora`, `mixto` o `null`.
 *
 * Por qué existe: "sin conexión" tapaba dos fallas que se reparan distinto y por
 * gente distinta. Medido el 5-oct-2026 en producción, a la misma hora:
 *
 *  - Las Carmelitas: la PC latía (último latido hacía 22 s, `connection_status:
 *    "connected"`) pero 232 de 232 lecturas de historial de las últimas 3,9 h
 *    fallaron con "The device is off-line" (SDK err 7). La PC responde, la
 *    CONTROLADORA no — y la tarjeta decía "En vivo".
 *  - Barranca del Conde: 38,8 h sin un solo latido. Ahí es la PC.
 *
 * `null` es "no hay nada caído o no se puede saber", y cae ahí todo lo que no
 * sea una afirmación positiva del backend:
 *
 *  - backend viejo, que no manda el campo (`undefined`);
 *  - un valor que este cliente no conoce: el vocabulario lo define el backend y
 *    este archivo es una copia, mismo criterio que `yardLinkState` — un cuarto
 *    valor tiene que degradar a "sin causa", no pintar una clave de traducción
 *    cruda en la tarjeta;
 *  - un `link` sin puertas caídas. El backend ya manda `null` en esos estados,
 *    pero la condición se repite acá porque es lo que impide que la tarjeta
 *    escriba "la controladora no responde" al lado de una píldora verde si
 *    alguna vez los dos campos llegan desfasados.
 */
export function yardDownCause(
  health: YardHealth | null | undefined,
): YardDownCause | null {
  if (!health || !yardLinkHasOutage(yardLinkState(health))) return null;
  const cause = health.down_cause;
  // Lista y no `in` sobre un objeto: `in` acepta claves heredadas ("toString").
  return cause && DOWN_CAUSES.includes(cause) ? cause : null;
}

/**
 * El último error de lectura contra la controladora, sólo cuando explica algo.
 *
 * Con causa `pc` el texto puede seguir en el doc desde una falla vieja, y
 * mostrarlo debajo de "la PC no responde" apunta al equipo equivocado. Sólo
 * acompaña a `controladora` y `mixto`.
 */
export function yardControllerError(
  health: YardHealth | null | undefined,
): string | null {
  const cause = yardDownCause(health);
  if (cause !== "controladora" && cause !== "mixto") return null;
  const raw = health?.controller_last_error;
  if (typeof raw !== "string") return null;
  return raw.trim() || null;
}

/**
 * El desglose por puerta, con el denominador de CADA número.
 *
 * Los cuatro contadores de `computeYardHealth` NO son cuatro categorías
 * paralelas, y presentarlos como una lista de pares es una mentira aritmética:
 *
 *   doors_monitored = puertas que alguna vez latieron (hasHeartbeat)
 *   doors_unknown   = doors_total − doors_monitored        ← el complemento
 *   doors_down      = monitoreadas SIN conexión ahora      ← un SUBCONJUNTO
 *
 * (yardHealthService.js:363-365). O sea que la partición de `doors_total` es
 * `monitored + withoutHeartbeat`, y `down` vive DENTRO de `monitored`. Escribir
 * "2 puertas · 1 monitoreadas · 1 sin conexión · 1 sin latido" invita a sumar
 * 1+1+1 contra 2, y sobre todo lee "1 sin conexión" contra las 2 puertas: es
 * exactamente el error que la tarjeta de #499 ya nombra por escrito —"meterla en
 * el denominador convierte un «1 de 1 sin conexión» en un tranquilizador «1 de
 * 2»" (YardOperationSummary.tsx:78-81)—, que ahí se resolvió con
 * `yards.health.doorsDown` = "{{down}} de {{monitored}} sin conexión". La modal
 * y la tarjeta no pueden contradecirse dos líneas más abajo.
 *
 * `down: null` cuando ninguna puerta late: con `monitored = 0`, `doors_down`
 * vale 0 por falta de muestra y no porque estén todas bien, y "0 sin conexión"
 * ahí se lee como "todo en orden" cuando es lo contrario. Hoy es el caso
 * dominante: 6 de los 7 patios de producción no tienen ni un controlador
 * registrado (medido el 4-sep-2026), y un controlador en modo `direct` tampoco
 * late nunca.
 *
 * `null` con una sola puerta: el desglose repetiría lo que ya dice la píldora.
 */
export interface YardDoorsBreakdown {
  total: number;
  /** Puertas con latido. `monitored + withoutHeartbeat === total`. */
  monitored: number;
  /** Nunca latieron: el complemento de `monitored`, no un tercer estado. */
  withoutHeartbeat: number;
  /** Caídas DENTRO de `monitored`. `null` si no hay ninguna monitoreada. */
  down: number | null;
}

export function yardDoorsBreakdown(
  health: YardHealth | null | undefined,
): YardDoorsBreakdown | null {
  if (!health || health.doors_total <= 1) return null;
  return {
    total: health.doors_total,
    monitored: health.doors_monitored,
    withoutHeartbeat: health.doors_unknown,
    down: health.doors_monitored > 0 ? health.doors_down : null,
  };
}

/**
 * "3 d 4 h", "12 min", "45 s" — cuánto hace que empezó el corte.
 *
 * Dos unidades como máximo y nunca una precisión que el dato no tiene:
 * `connection_lost_at` lo estampa markStaleDevices recién tras 90 s de silencio
 * y con un cron de 1 min, así que llega entre 90 y 150 s tarde. Mostrar
 * segundos por encima de un minuto sería inventar exactitud.
 */
export function formatAge(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return `${Math.floor(ms / 1000)} s`;
  if (totalMin < 60) return `${totalMin} min`;
  const totalHours = Math.floor(totalMin / 60);
  if (totalHours < 24) {
    const min = totalMin % 60;
    return min > 0 ? `${totalHours} h ${min} min` : `${totalHours} h`;
  }
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
}

// ─── Conteos de operación (#500) ──────────────────────────────────────────────

/**
 * La partición de `visits` que devuelve GET /yardMetrics. Los cubos suman
 * exactamente `total`, en el orden de precedencia de `deriveInspectionStatus`.
 */
export interface YardMetricsVisits {
  total: number;
  provider: number;
  missing_vehicle: number;
  with_exit: number;
  without_exit: number;
  without_exit_marked: number;
  without_exit_stale: number;
  without_exit_open: number;
  without_exit_unaged: number;
  closed_with_capture_fields: number;
  closed_without_capture_fields: number;
}

/**
 * TODA métrica agregable de `/yardMetrics` viaja como `{sum, n, avg}` y nunca
 * como `avg` suelto (yardMetricsService.js:210-225). Ese `n` no es decorativo:
 * es el denominador que el panel de #501 TIENE que escribir en pantalla, y es
 * distinto para cada métrica. `avg` llega en `null` —no en 0— cuando `n` es 0,
 * porque un patio que no descargó nada y un patio que descargó 0 L no son la
 * misma respuesta.
 */
export interface YardAggregate {
  sum: number | null;
  n: number;
  avg: number | null;
}

export interface YardWaterTypeBucket {
  water_type: string | null;
  label: string;
  known: boolean;
  n: number;
  share: number | null;
  litres: YardAggregate;
  /**
   * El corte de calidad POR tipo de agua, al lado de `litres` y con el mismo
   * denominador `n` del cubo. Llega como `{ph, conductividad, turbidez}`; se lee
   * con `waterTypeQuality` en vez de tipearlo cerrado, porque el catálogo de
   * parámetros lo define el servidor y este archivo es una copia del contrato.
   */
  water_quality?: unknown;
}

export interface YardTopGroup {
  /** `null` es el cubo "Sin grupo", y viaja en null para que nadie navegue a un grupo que no existe. */
  group_id: string | null;
  group_name: string;
  n: number;
  litres: YardAggregate;
}

export interface YardTopVehicle {
  vehicle_id: string | null;
  vehicle_plate: string | null;
  card_number: string | null;
  n: number;
  litres: YardAggregate;
  dwell_seconds: YardAggregate;
}

/**
 * Todo lo que el detalle (#501) consume además de `visits` es OPCIONAL en el
 * tipo, y no por prolijidad: la tarjeta y el panel comparten este contrato pero
 * no el mismo despliegue. Un backend anterior a #500 devuelve sólo `visits`, y
 * con los campos obligatorios el panel se caería en el primer `.avg` en vez de
 * mostrar el bloque que sí puede. `water_quality` es el caso extremo y por eso
 * entra como `unknown`: al 4-sep-2026 el servidor lo devuelve `null` fijo
 * (yardMetricsService.js:595) y el cálculo se está escribiendo en paralelo.
 */
export interface YardMetricsResponse {
  yard_id: string;
  yard_name?: string | null;
  visits: YardMetricsVisits;
  missing_exit_after_hours: number;
  computed_at: number;
  scan?: { inspections_read: number; truncated: boolean; max_inspections: number };
  groups_resolution?: {
    vehicles_seen: number;
    devices_resolved: number;
    multi_group_vehicles: number;
    truncated: boolean;
  };
  water_types?: {
    catalog: string[];
    top: string | null;
    /** Denominador del reparto: visitas NO canceladas. No es `visits.total`. */
    n: number;
    unknown_values: string[];
    breakdown: YardWaterTypeBucket[];
  };
  litres?: {
    per_visit: YardAggregate;
    per_vehicle: YardAggregate;
    unparseable: number;
    missing: number;
    excluded_cancelled: number;
  };
  dwell_seconds?: YardAggregate & {
    dropped_negative: number;
    unmeasurable: number;
  };
  top_groups?: YardTopGroup[];
  top_vehicles?: YardTopVehicle[];
  top_limit?: number;
  range?: {
    date_from: string | null;
    date_to: string | null;
    from_ms: number | null;
    to_ms: number | null;
    time_zone: string;
    anchor: string;
  };
  /** #502 cerró y los tres valores ya se capturan como número, pero el agregado
   *  todavía no: hoy llega `null` fijo. Se lee con `readWaterQuality`. */
  water_quality?: unknown;
}

export type YardStatusCounts = Record<InspectionStatus, number>;

/**
 * `visits` → los seis conteos del vocabulario del cliente.
 *
 * La regla vive en UN solo lado y ese lado es #500: el servidor recibe el
 * `missing_exit_after_hours` del cliente (8 h, MISSING_EXIT_AFTER_HOURS) y hace
 * la aritmética del corte por antigüedad con ese número, para que la tarjeta y
 * la grilla del detalle no digan cosas distintas por haberse pintado con
 * minutos de diferencia. Acá sólo se re-etiquetan cubos ya calculados; no se
 * vuelve a derivar nada ni se mira el reloj.
 *
 * El mapeo sigue exactamente la cadena de `deriveInspectionStatus`:
 *
 *   provider                          → cancelled
 *   missing_vehicle                   → missing_vehicle
 *   without_exit_marked | _stale      → missing_exit   (hasLostExit: la marca
 *                                       del backend gana, si no, la edad)
 *   without_exit_open | _unaged       → pending_exit   (sin entry legible
 *                                       hasLostExit devuelve false)
 *   closed_with_capture_fields        → complete
 *   closed_without_capture_fields     → incomplete
 *
 * La única imprecisión conocida está en el último par y es del contrato, no de
 * acá: `closed_with_capture_fields` es una COTA SUPERIOR de "complete" —tiene
 * tipo de agua y volumen, le faltaría verificar las tres fotos obligatorias,
 * que viven en la subcolección `evidences` y no se pueden leer sin un N+1
 * (yardMetricsService.js:40-56)—. O sea que una inspección a la que le falta
 * una foto sale "Completa" en la tarjeta e "Incompleta" en el historial. La
 * tarjeta lo dice al pasar el mouse; el arreglo de fondo es desnormalizar el
 * conteo de fotos en el documento, y es otra issue.
 */
export function visitsToStatusCounts(visits: YardMetricsVisits): YardStatusCounts {
  return {
    complete: visits.closed_with_capture_fields || 0,
    pending_exit: (visits.without_exit_open || 0) + (visits.without_exit_unaged || 0),
    missing_exit: (visits.without_exit_marked || 0) + (visits.without_exit_stale || 0),
    missing_vehicle: visits.missing_vehicle || 0,
    incomplete: visits.closed_without_capture_fields || 0,
    cancelled: visits.provider || 0,
    // `/yardMetrics` no separa las visitas del tag de proveedores: su cubo
    // `provider` cuenta por `inspection_type`, que comparten el proveedor
    // legacy y el del tag. Hasta que el servicio las parta, la tarjeta las
    // sigue sumando en "Cancelada" y estos dos quedan en cero —que la tira no
    // pinta— en vez de inventar un reparto que el servidor no hizo.
    provider_pending: 0,
    provider_complete: 0,
  };
}

/**
 * El día de hoy EN EL PATIO, como "YYYY-MM-DD".
 *
 * No es el día del navegador. `/yardMetrics` interpreta "YYYY-MM-DD" como hora
 * de pared del patio (yardDateRange.js), así que mandar el día local del
 * operador desde otra zona pediría un día distinto del que la tarjeta rotula.
 * Es el mismo corrimiento de 6 h que ese módulo existe para arreglar, sólo que
 * originado en el cliente.
 *
 * El respaldo es "America/Mexico_City", el mismo DEFAULT_YARD_TIME_ZONE del
 * backend, y se aplica también si `Intl` rechaza la zona: `timeZone` es un
 * campo de formulario y una zona inválida haría explotar el render de la
 * tarjeta entera por una etiqueta de fecha.
 */
export const DEFAULT_YARD_TIME_ZONE = "America/Mexico_City";

export function yardLocalDay(
  timeZone: string | undefined | null,
  now: Date = new Date(),
): string {
  const zone = timeZone || DEFAULT_YARD_TIME_ZONE;
  try {
    return formatDayIn(zone, now);
  } catch {
    return formatDayIn(DEFAULT_YARD_TIME_ZONE, now);
  }
}

// Construir un Intl.DateTimeFormat no es gratis y esto corre una vez por patio
// en CADA render de la lista (hasta 10 por página, y la página se repinta con
// cada refresco del polling de 30 s). El formateador depende sólo de la zona,
// así que se cachea por zona.
const dayFormatters = new Map<string, Intl.DateTimeFormat>();

function formatDayIn(timeZone: string, now: Date): string {
  let formatter = dayFormatters.get(timeZone);
  if (!formatter) {
    // `formatToParts` y no `toLocaleDateString("en-CA")`: la forma del
    // resultado de un locale depende del ICU que traiga el navegador, y acá el
    // formato es parte del contrato con el backend, no una preferencia de
    // presentación. Si la zona es inválida, el constructor tira acá —antes de
    // entrar al mapa— y `yardLocalDay` cae en la zona por defecto.
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    dayFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

// ─── Detalle de operación (#501) ─────────────────────────────────────────────
//
// Todo lo decidible del panel de la modal vive acá y no en el .tsx, por una
// razón de entorno y no de gusto: un worktree no recibe `apps/v5/.env`
// (gitignoreado), así que cualquier componente que importe transitivamente
// `utils/api` → firebase revienta al COLECTAR con `auth/invalid-api-key` y no se
// puede testear. Este módulo no importa nada; por eso es el único lugar donde
// las reglas de "cuál es el denominador" se pueden probar.

/**
 * Cuántas barras antes de "Otros".
 *
 * #501 lo pide explícito —"top 10 + otros, no una torta de 40 rebanadas"— y el
 * endpoint lo admite: `top` es un query param acotado a MAX_TOP_LIMIT = 20
 * (yardMetricsSchema.js), con default 5. La tarjeta se conforma con el default
 * porque no grafica; el panel pide 10.
 *
 * El costo de pedir 10 en vez de 5 es de respuesta, no de lectura: el servidor
 * agrega sobre TODAS las inspecciones del rango igual y sólo recorta el arreglo
 * al final (aggregateInspections → `.slice(0, topLimit)`).
 */
export const OPERATION_TOP_N = 10;

// ── Rango ────────────────────────────────────────────────────────────────────

export type YardRangePreset = "today" | "last7" | "last30" | "custom";

export interface YardDayRange {
  /** "YYYY-MM-DD", hora de pared del PATIO. Ver yardLocalDay. */
  from: string;
  to: string;
}

/**
 * Corre un "YYYY-MM-DD" N días, en el calendario y no en el reloj.
 *
 * Aritmética en UTC a propósito: el día ya viene expresado en la zona del patio
 * (`yardLocalDay`), así que acá sólo hay que contar casillas de calendario. Un
 * `new Date("2026-09-04")` local más 86.400.000 ms se corre solo en los dos
 * domingos del año en que cambia el horario de verano —México dejó de aplicarlo
 * en 2022, pero `timeZone` es un campo de formulario con el catálogo entero de
 * TIMEZONE_OPTIONS detrás, y varias de esas zonas sí lo aplican—. Con Date.UTC
 * no hay DST que valga.
 */
const YARD_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function shiftYardDay(day: string, deltaDays: number): string {
  const m = YARD_DAY_RE.exec(day);
  if (!m) return day;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const shifted = new Date(ms + deltaDays * 86_400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate(),
  )}`;
}

/**
 * El rango de cada preajuste, anclado al día EN CURSO del patio.
 *
 * "El bloque abre en el día en curso" es criterio de aceptación de #501 y el
 * criterio del hito para el módulo entero: la tarjeta de #499 ya abre en hoy, y
 * un panel que abriera en "últimos 30 días" mostraría números que no cierran
 * con la tarjeta que está detrás de la misma modal.
 *
 * "7 días" es hoy más los 6 anteriores, no hoy más 7. Se elige así porque el
 * rango del endpoint es INCLUSIVO en los dos extremos (buildYardDateRange lleva
 * `date_to` al final del día del patio), de modo que "7 días" devuelve
 * exactamente siete días de calendario. Con -7 serían ocho.
 */
export function yardPresetRange(
  preset: Exclude<YardRangePreset, "custom">,
  timeZone: string | undefined | null,
  now: Date = new Date(),
): YardDayRange {
  const today = yardLocalDay(timeZone, now);
  if (preset === "today") return { from: today, to: today };
  const days = preset === "last7" ? 7 : 30;
  return { from: shiftYardDay(today, -(days - 1)), to: today };
}

/**
 * Un rango personalizado que el operador escribió al revés no puede salir como
 * "sin resultados": los dos `input type="date"` son independientes y mover
 * primero el extremo inferior por encima del superior es el orden natural de
 * tipeo. Con `from > to` el backend arma un `where(">=", from)` y un
 * `where("<=", to)` que no se cruzan nunca y devuelve todo en cero, que es
 * indistinguible de "ese patio no tuvo movimiento" — exactamente lo que ese
 * endpoint se propone no hacer (yardMetricsController.js).
 */
export function normalizeYardRange(from: string, to: string): YardDayRange {
  return from > to ? { from: to, to: from } : { from, to };
}

/**
 * El rango personalizado, o `null` mientras esté a medio escribir.
 *
 * Un `<input type="date">` emite `""` mientras la fecha está incompleta —y
 * también cuando se la limpia con la X—, así que este estado se atraviesa
 * TECLEANDO, no en un borde raro. Y un extremo vacío no es un filtro benigno de
 * menos: `parseYardDateBound("")` devuelve null (yardDateRange.js) y el backend
 * arma la consulta SIN ese límite, o sea que barre el histórico entero del patio
 * —hasta el techo de 5.000 inspecciones— y devuelve esos totales, que el panel
 * rotularía con el rango que el operador eligió. Un total inventado es peor que
 * un panel en blanco, así que acá no hay rango y la consulta no sale.
 *
 * Se valida la FORMA y no sólo que haya texto: `parseYardDateBound` acepta epoch
 * y ISO con zona, de modo que un "2026-9-1" no reventaría, se interpretaría como
 * otra cosa. El contrato de este selector es "YYYY-MM-DD" en hora del patio.
 */
export function yardCustomRange(from: string, to: string): YardDayRange | null {
  if (!YARD_DAY_RE.test(from) || !YARD_DAY_RE.test(to)) return null;
  return normalizeYardRange(from, to);
}

// ── Identidad de la unidad ───────────────────────────────────────────────────

export type YardUnitIdKind = "plate" | "vehicle" | "card" | "none";

export interface YardUnitIdentity {
  label: string;
  identifiedBy: YardUnitIdKind;
}

/**
 * Cómo se nombra una unidad en el ranking, y qué significa que se la nombre así.
 *
 * El SERVIDOR ya agrupa por tres campos en cascada —`veh:` → `plate:` → `card:`
 * (vehicleKeyOf, yardMetricsService.js:236-241)— y eso es lo que #501 subraya:
 * agrupar sólo por `vehicle_id` deja fuera justo las visitas `missing_vehicle`,
 * que son 10 de las 149 inspecciones de producción medidas el 3-sep-2026, y son
 * las que más importa vigilar. Acá sólo se ETIQUETA el cubo que ya vino.
 *
 * La etiqueta invierte la prioridad de la clave: primero la placa, porque es lo
 * que un operador lee en el costado del camión; el `vehicle_id` es un id de
 * Firestore que no le dice nada a nadie. La CLAVE sigue siendo la del servidor,
 * así que dos cubos distintos nunca se funden por compartir etiqueta.
 *
 * `identifiedBy: "card"` no es un detalle de presentación: un cubo que llegó
 * hasta el respaldo de la tarjeta es, por definición, una visita sin
 * `vehicle_id` NI `vehicle_plate` — o sea exactamente el estado
 * `missing_vehicle` (misma cadena de precedencia en deriveInspectionStatus y en
 * aggregateInspections). El panel lo marca para que "la tarjeta 12345 entró 9
 * veces y nadie sabe qué camión es" se vea, en vez de quedar como una fila más.
 */
export function yardUnitIdentity(
  vehicle: Pick<YardTopVehicle, "vehicle_id" | "vehicle_plate" | "card_number">,
  labels: { card: (n: string) => string; unidentified: string },
): YardUnitIdentity {
  if (vehicle.vehicle_plate) {
    return { label: vehicle.vehicle_plate, identifiedBy: "plate" };
  }
  if (vehicle.vehicle_id) {
    return { label: vehicle.vehicle_id, identifiedBy: "vehicle" };
  }
  if (vehicle.card_number) {
    return { label: labels.card(vehicle.card_number), identifiedBy: "card" };
  }
  return { label: labels.unidentified, identifiedBy: "none" };
}

// ── Barras: top N + "Otros" ──────────────────────────────────────────────────

export type YardBarKind = "normal" | "no_group" | "missing_vehicle" | "others";

export interface YardBarDatum {
  key: string;
  label: string;
  /** Visitas. Es el criterio de ranking del servidor (n, y litros para desempatar). */
  n: number;
  litres: number | null;
  kind: YardBarKind;
}

/**
 * El universo sobre el que se reparten las barras: visitas NO canceladas.
 *
 * `water_types.n` y no `visits.total`, y la diferencia es exacta: en
 * aggregateInspections el `continue` de proveedor está ANTES de los tres
 * acumuladores (tipo de agua, grupo y unidad), así que los tres comparten
 * denominador y ninguno incluye las canceladas. `visits.total` sí las incluye,
 * y usarlo inflaría el cubo "Otros" con visitas que no están en ningún cubo.
 *
 * Al 3-sep-2026 producción tiene 0 visitas de proveedor, así que hoy los dos
 * números coinciden y el error sería invisible. Por eso se elige el correcto
 * ahora y no cuando aparezca el primer proveedor.
 *
 * El `>= fromTop` es el cinturón: si un despliegue viejo no manda `water_types`
 * —o manda un `n` menor que lo que ya suman las barras, que sería un contrato
 * roto— se cae a la suma del top y "Otros" queda en cero, que es lo único que
 * se puede afirmar sin el universo.
 */
function nonCancelledUniverse(
  metrics: YardMetricsResponse,
  entries: { n: number }[],
): number {
  const declared = metrics.water_types?.n;
  const fromTop = entries.reduce((acc, e) => acc + e.n, 0);
  return typeof declared === "number" && declared >= fromTop ? declared : fromTop;
}

/**
 * Recorta a `topN` y agrega el resto en UNA barra "Otros".
 *
 * "Otros" se calcula por RESTA contra el universo, no sumando lo que sobró del
 * arreglo: el servidor ya recortó a `top_limit` antes de responder, así que lo
 * que sobra no viene. La resta es exacta porque cada visita no cancelada cae en
 * exactamente un cubo (de grupo y de unidad), que es la misma propiedad de
 * partición sobre la que se apoya el rollup por empresa para sumar patios.
 *
 * Lo que "Otros" NO puede decir es CUÁNTOS grupos o unidades agrupa: el
 * endpoint devuelve el top recortado y no la cardinalidad total. Por eso la
 * barra se rotula con visitas y nunca con "N grupos más" — sería inventarlo.
 */
function withOthers(
  bars: YardBarDatum[],
  universe: number,
  topN: number,
  othersLabel: string,
): YardBarDatum[] {
  const top = bars.slice(0, topN);
  const shown = top.reduce((acc, b) => acc + b.n, 0);
  const rest = universe - shown;
  if (rest <= 0) return top;
  return [
    ...top,
    { key: "__others__", label: othersLabel, n: rest, litres: null, kind: "others" },
  ];
}

export function yardGroupBars(
  metrics: YardMetricsResponse,
  labels: { others: string; noGroup: string },
  topN: number = OPERATION_TOP_N,
): YardBarDatum[] {
  const groups = metrics.top_groups ?? [];
  const bars: YardBarDatum[] = groups.map((g) => ({
    // El cubo "Sin grupo" viaja con `group_id: null` y es una CATEGORÍA, no un
    // descarte: #500 y #501 lo piden visible con todas las letras. Hoy pesa: el
    // vínculo vehículo→grupo se resuelve por `groups.devices_ids` →
    // `devices.vehicle_id`, y toda unidad que no esté en ese join cae acá.
    key: g.group_id ?? "__no_group__",
    label: g.group_id === null ? labels.noGroup : g.group_name,
    n: g.n,
    litres: g.litres?.sum ?? null,
    kind: g.group_id === null ? ("no_group" as const) : ("normal" as const),
  }));
  return withOthers(bars, nonCancelledUniverse(metrics, groups), topN, labels.others);
}

export function yardUnitBars(
  metrics: YardMetricsResponse,
  labels: { others: string; card: (n: string) => string; unidentified: string },
  topN: number = OPERATION_TOP_N,
): YardBarDatum[] {
  const vehicles = metrics.top_vehicles ?? [];
  const bars: YardBarDatum[] = vehicles.map((v, i) => {
    const identity = yardUnitIdentity(v, labels);
    return {
      // La clave replica la del servidor (`veh:`/`plate:`/`card:`) para que dos
      // unidades distintas con la misma etiqueta no colisionen como key de
      // React. El índice cierra el caso "ni id, ni placa, ni tarjeta", que el
      // servidor mete todo en un único cubo "unidentified".
      key: v.vehicle_id
        ? `veh:${v.vehicle_id}`
        : v.vehicle_plate
          ? `plate:${v.vehicle_plate}`
          : v.card_number
            ? `card:${v.card_number}`
            : `unidentified:${i}`,
      label: identity.label,
      n: v.n,
      litres: v.litres?.sum ?? null,
      kind:
        identity.identifiedBy === "card" || identity.identifiedBy === "none"
          ? ("missing_vehicle" as const)
          : ("normal" as const),
    };
  });
  return withOthers(bars, nonCancelledUniverse(metrics, vehicles), topN, labels.others);
}

/**
 * El universo que la gráfica REALMENTE dibujó.
 *
 * Se suma sobre las barras y no sobre `water_types.n` a propósito: así la
 * leyenda no puede describir un universo distinto del que se pintó ni siquiera
 * cuando el contrato viene roto y `withOthers` cae al respaldo de la suma del
 * top (ver `nonCancelledUniverse`).
 *
 * Hace falta escribirlo porque las barras y la tira de píldoras de arriba NO
 * cuentan lo mismo: las píldoras muestran `visits.total`, que incluye las
 * canceladas, y las barras se reparten sólo las NO canceladas (el `continue` de
 * proveedor está antes de los acumuladores de grupo y de unidad). Al 3-sep-2026
 * producción tiene 0 visitas de proveedor, así que hoy los dos números coinciden
 * y la diferencia es invisible — que es justo el motivo de declararla ahora y no
 * el día que entre el primer proveedor y alguien tenga que explicar por qué la
 * gráfica suma menos que el total de arriba.
 */
export function yardBarsTotal(bars: YardBarDatum[]): number {
  return bars.reduce((acc, b) => acc + b.n, 0);
}

// ── Denominadores declarados ─────────────────────────────────────────────────

export interface YardDwellSample {
  /** Visitas efectivamente medidas: cerradas, con los dos instantes legibles y duración no negativa. */
  n: number;
  /** Visitas con lectura de la lectora de SALIDA. El denominador que se escribe en pantalla. */
  closed: number;
  /** Visitas sin salida, fuera del promedio. No duran infinito: no se sabe cuánto duraron. */
  withoutExit: number;
  /** Cerradas pero sin instante legible. */
  unmeasurable: number;
  /** Salida anterior a la entrada. 0 casos medidos el 3-sep-2026 en las dos bases. */
  droppedNegative: number;
  seconds: number | null;
}

/**
 * El `n` del tiempo dentro del patio, con su universo.
 *
 * Es el ejemplo textual de #501 —"43 min (n = 28 de 51 visitas cerradas)"— y el
 * motivo por el que el panel entero declara denominadores: 11 de las 20 tags de
 * Acuapue nunca fueron leídas por la lectora de salida
 * (yardInspectionsStore.ts:176-179), y medido el 3-sep-2026 hay 28 de 149
 * visitas abiertas en producción y 22 de 119 en dev. Un promedio sobre media
 * muestra sin declararlo no dice "tardan 43 min", dice "de las que sabemos,
 * tardan 43 min", y son afirmaciones distintas.
 *
 * `closed` es `visits.with_exit`, el corte crudo por lectora de salida, y NO la
 * suma de los cubos cerrados de la partición: `closed_with_capture_fields +
 * closed_without_capture_fields` excluye a los proveedores y a las visitas sin
 * vehículo que además salieron, y la estancia sí las mide (se calcula antes del
 * corte por proveedor, a propósito: un proveedor no descarga pero ocupa el patio
 * un rato real).
 */
export function yardDwellSample(metrics: YardMetricsResponse): YardDwellSample {
  const dwell = metrics.dwell_seconds;
  return {
    n: dwell?.n ?? 0,
    closed: metrics.visits?.with_exit ?? 0,
    withoutExit: metrics.visits?.without_exit ?? 0,
    unmeasurable: dwell?.unmeasurable ?? 0,
    droppedNegative: dwell?.dropped_negative ?? 0,
    seconds: dwell?.avg ?? null,
  };
}

export interface YardLitresSample {
  n: number;
  /** Visitas no canceladas: el universo de los litros. */
  universe: number;
  /** Capturado pero ilegible: "1.000" es ambiguo por 1000x y se rechaza a propósito. */
  unparseable: number;
  /** Sin volumen capturado. */
  missing: number;
  /** Visitas de proveedor, fuera del promedio: cruzan el patio y no descargan. */
  excludedCancelled: number;
}

/**
 * El cubo del tipo de agua más usado, o `null` cuando NINGUNO se usó.
 *
 * El servidor deja al cubo "Sin tipo" fuera de la carrera a propósito —"si
 * ganara, la tarjeta diría «el agua más usada es: ninguna», que no es una
 * respuesta" (yardMetricsService.js)— y en ese caso manda `top: null`. Pero el
 * cubo sin tipo TAMBIÉN viaja con `water_type: null` dentro de `breakdown`, así
 * que buscar `top` con un `find` por igualdad lo devuelve por la ventana y el
 * KPI termina diciendo "Tipo de agua más usado: Sin tipo".
 *
 * No es un borde: medido el 4-sep-2026, 157 de las 160 inspecciones de
 * producción no tienen `water_type` (1 `agua_residual`, 2 `agua_en_proceso`), o
 * sea que casi cualquier rango de un día no tiene ni una visita tipificada y
 * cae exactamente en este caso. Con `null` el panel escribe un guion, que es lo
 * único cierto; el reparto completo —incluido el 98% sin tipo— se ve igual en la
 * tabla de abajo, donde "Sin tipo" es una fila y no una respuesta.
 */
export function yardTopWaterType(
  metrics: YardMetricsResponse,
): YardWaterTypeBucket | null {
  const top = metrics.water_types?.top;
  if (top == null) return null;
  return metrics.water_types?.breakdown.find((b) => b.water_type === top) ?? null;
}

export function yardLitresVisitSample(metrics: YardMetricsResponse): YardLitresSample {
  const litres = metrics.litres;
  return {
    n: litres?.per_visit?.n ?? 0,
    universe: metrics.water_types?.n ?? 0,
    unparseable: litres?.unparseable ?? 0,
    missing: litres?.missing ?? 0,
    excludedCancelled: litres?.excluded_cancelled ?? 0,
  };
}

/**
 * Cuántas unidades distintas hubo en el rango — o `null` cuando no se puede
 * saber.
 *
 * El endpoint NO devuelve la cardinalidad de unidades: devuelve `top_vehicles`
 * ya recortado a `top_limit`. Si el arreglo llegó CORTO, es la lista completa y
 * su largo es el universo exacto; si llegó lleno hasta el tope, hay más y no se
 * sabe cuántas. En ese caso esto devuelve `null` y el panel escribe "n = 7
 * unidades" sin denominador, en vez de inventar uno.
 *
 * `groups_resolution.vehicles_seen` NO sirve como sustituto aunque lo parezca:
 * cuenta `vehicle_id` distintos incluyendo las visitas canceladas y excluyendo
 * los cubos que sólo tienen placa o sólo tarjeta — o sea, otro universo.
 */
export function yardDistinctUnitsUniverse(metrics: YardMetricsResponse): number | null {
  const vehicles = metrics.top_vehicles;
  const limit = metrics.top_limit;
  if (!vehicles || typeof limit !== "number") return null;
  return vehicles.length < limit ? vehicles.length : null;
}

// ── Formato ──────────────────────────────────────────────────────────────────

/**
 * El tiempo de estancia con el mismo formateador que la antigüedad del corte.
 *
 * Reusar `formatAge` no es pereza: sus dos unidades máximas y su negativa a
 * mostrar segundos por encima del minuto son exactamente lo que este número
 * necesita. Las visitas reales de descarga en Acuapue duran entre 13 y 60
 * minutos medidas contra el log del controlador (10:25→11:22, 12:10→12:24), así
 * que el caso normal cae entero en el tramo de minutos.
 */
export function formatDwell(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  return formatAge(seconds * 1000);
}

// Los únicos volúmenes capturados en toda la historia de las dos bases son
// "83.2" (producción) y "7.5" (dev), medidos el 3-sep-2026. Un decimal es toda
// la precisión que el dato tiene; el agregado del servidor redondea a tres y
// mostrar esos tres sugeriría una exactitud que la manguera no tiene.
const litresFormatter = new Intl.NumberFormat("es-MX", {
  maximumFractionDigits: 1,
});

export function formatLitres(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return litresFormatter.format(value);
}

/**
 * El `sum` de un agregado, o `null` cuando no se midió NADA.
 *
 * El contrato de `/yardMetrics` dice que "`avg` llega en null —no en 0— cuando
 * `n` es 0", y es cierto: lo hace `finish()`. Pero `sum` NO sigue esa regla y es
 * fácil creer que sí. `finish` devuelve `sum: round(acc.sum, decimals)` sobre un
 * acumulador que nace en `{sum: 0, n: 0}`, y `round(0, 3)` es 0. O sea que un
 * agregado sin una sola muestra viaja como `{sum: 0, n: 0, avg: null}` — el
 * mismo cuerpo que el servidor manda hoy para casi cualquier rango.
 *
 * Sin esta puerta el KPI "Litros extraídos (total)" pintaba **0** al lado de un
 * promedio en guion, y cada fila de la tabla por tipo de agua un "0" en la
 * columna Total con un "—" en la de Promedio. Un 0 ahí no se lee como "nadie
 * capturó un volumen": se lee como "midieron y no salió nada", que es una
 * afirmación sobre la operación del patio y es falsa.
 *
 * No es un borde: medido el 4-sep-2026, `volumen_extraido` está capturado en 1
 * de 160 inspecciones de producción y 1 de 130 de dev, así que CUALQUIER rango
 * de un día que no sea el de esa única visita entra por acá.
 *
 * Un 0 con `n > 0` sí sale como 0, y esa es toda la distinción: "0" es una
 * captura legítima que el servidor acepta a propósito (parseLitres: "Un 0 real
 * («0») sí se acepta: es una medición, no una ausencia").
 */
export function yardAggregateSum(
  aggregate: YardAggregate | null | undefined,
): number | null {
  if (!aggregate || aggregate.n <= 0) return null;
  const { sum } = aggregate;
  return typeof sum === "number" && Number.isFinite(sum) ? sum : null;
}

// ── Calidad del agua (#502) ──────────────────────────────────────────────────

/**
 * Un parámetro de calidad, tal como lo agrega el servidor.
 *
 * Misma forma `{sum, n, avg}` que el resto del servicio, más los dos contadores
 * de descarte. Vale la invariante `n + unparseable + missing === water_quality.n`:
 * cada visita no cancelada aporta exactamente una de las tres cosas a cada
 * parámetro, y por eso el `n = X de Y` de la pantalla se arma sin salir del
 * bloque.
 */
export interface YardQualityParameter {
  key: string;
  /** Etiqueta del servidor ("pH", "Conductividad", "Turbidez"). */
  label: string;
  /** "µS/cm", "NTU", o vacío para el pH, que es adimensional. */
  unit: string;
  sum: number | null;
  n: number;
  avg: number | null;
  unparseable: number;
  missing: number;
}

export interface YardWaterQuality {
  /** Universo: visitas NO canceladas. El MISMO denominador que los litros. */
  n: number;
  /** Visitas de proveedor excluidas. Sale del mismo `continue` que el de litros,
   *  así que los dos números no pueden divergir. */
  excludedCancelled: number;
  /** En el orden de `catalog`, no en el de las claves del objeto. */
  parameters: YardQualityParameter[];
}

function readQualityAggregate(raw: unknown): YardAggregate | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const n = typeof obj.n === "number" ? obj.n : 0;
  const avg = typeof obj.avg === "number" ? obj.avg : null;
  return { sum: typeof obj.sum === "number" ? obj.sum : null, n, avg };
}

/**
 * Lee `water_quality` DEFENSIVAMENTE.
 *
 * Devuelve `null` sólo cuando el campo no es un objeto — o sea, contra el
 * backend anterior a este agregado, que devolvía `water_quality: null` fijo
 * (yardMetricsService.js:595). Con la respuesta nueva SIEMPRE devuelve algo,
 * aunque los tres parámetros vengan en cero, y eso es deliberado:
 *
 * Medido el 4-sep-2026 contra las dos bases, **ninguna** inspección tiene pH,
 * conductividad ni turbidez — el campo no existe en el documento: 160 de 160 en
 * producción y 130 de 130 en dev caen en `missing`, con los tres parámetros en
 * `n: 0`. La captura de #502 se desplegó ese mismo día. O sea que "0 de 161
 * visitas" no es un hueco vacío: es el único dato que le dice a operación que el
 * formulario nuevo todavía no se está usando. Esconder el bloque cuando `n = 0`
 * volvería indistinguible "nadie capturó una lectura" de "esta función no
 * existe", que es exactamente la confusión que el panel viene a evitar.
 *
 * Lo que sí es innegociable: `avg: null` con `n: 0` NO se pinta como 0. Un
 * promedio de cero muestras no es cero, es la ausencia de muestras — la misma
 * regla que ya rige `finish()` en el servidor.
 *
 * El orden lo manda `catalog` y no `Object.keys(parameters)`: el orden de claves
 * de un objeto sobrevive el JSON, pero es un accidente del serializador y no un
 * contrato. Un parámetro que llegue fuera del catálogo se agrega al final en vez
 * de desaparecer.
 */
export function readWaterQuality(raw: unknown): YardWaterQuality | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const params = obj.parameters;
  if (!params || typeof params !== "object") return null;
  const byKey = params as Record<string, unknown>;

  const catalog = Array.isArray(obj.catalog)
    ? obj.catalog.filter((k): k is string => typeof k === "string")
    : [];
  const extras = Object.keys(byKey).filter((k) => !catalog.includes(k));

  const parameters: YardQualityParameter[] = [];
  [...catalog, ...extras].forEach((key) => {
    const entry = byKey[key];
    if (!entry || typeof entry !== "object") return;
    const p = entry as Record<string, unknown>;
    parameters.push({
      key,
      // La etiqueta y la unidad las manda el servidor porque es quien sabe qué
      // parámetro es; el cliente sólo la traduce si tiene una traducción propia.
      label: typeof p.label === "string" ? p.label : key,
      unit: typeof p.unit === "string" ? p.unit : "",
      sum: typeof p.sum === "number" ? p.sum : null,
      n: typeof p.n === "number" ? p.n : 0,
      avg: typeof p.avg === "number" ? p.avg : null,
      unparseable: typeof p.unparseable === "number" ? p.unparseable : 0,
      missing: typeof p.missing === "number" ? p.missing : 0,
    });
  });

  if (parameters.length === 0) return null;

  return {
    n: typeof obj.n === "number" ? obj.n : 0,
    excludedCancelled:
      typeof obj.excluded_cancelled === "number" ? obj.excluded_cancelled : 0,
    parameters,
  };
}

/**
 * El promedio de un parámetro DENTRO de un tipo de agua.
 *
 * El corte por tipo viaja en `water_types.breakdown[].water_quality`, al lado de
 * `litres` y con el mismo denominador `breakdown[].n`, así que la fila del tipo
 * de agua ya trae todo lo que hace falta para declarar el `n` sin cruzar datos
 * de dos lugares.
 *
 * Hoy ese corte va a ser casi todo "Sin tipo": 157 de las 160 inspecciones de
 * producción no tienen `water_type` (1 `agua_residual`, 2 `agua_en_proceso`),
 * medido el 4-sep-2026. Por eso el cubo `null` se pinta como una fila más y no
 * como una nota al pie.
 */
export function waterTypeQuality(
  bucket: YardWaterTypeBucket,
  key: string,
): YardAggregate | null {
  const quality = bucket.water_quality;
  if (!quality || typeof quality !== "object") return null;
  return readQualityAggregate((quality as Record<string, unknown>)[key]);
}

// ── Cuándo vale la pena volver a preguntar por /yardMetrics ──────────────────
//
// El panel se quedaba MUERTO ante un fallo transitorio y esto es lo que lo
// arregla. La combinación exacta que lo producía: `retry: 0`, `refetchInterval:
// false` para todo rango que ya terminó, y `refetchOnWindowFocus: false` global
// (lib/queryClient.ts:9). Con las tres, un corte de red de un segundo sobre
// "1-31 de agosto" dejaba «Conteos no disponibles» pegado hasta que el usuario
// moviera el selector y volviera — y esa es la única forma de invalidar la
// clave, porque `from`/`to` entran en ella.
//
// Lo que NO se hace, y por qué:
//
//   • **No se sube `retry` a un número.** `/yardMetrics` devuelve 403 y 404 POR
//     DISEÑO: 403 es el recorte por patio del usuario (#495) y 404 es un patio
//     inexistente o de otra empresa. Las dos son la respuesta definitiva del
//     backend sobre este patio; no van a cambiar en 1 s ni en 30. Reintentar a
//     ciegas triplica el ruido contra el endpoint y no mejora nunca, que es
//     exactamente el motivo por el que `retry: 0` estaba ahí. La regla es al
//     revés: se reintenta cuando NO SE SUPO —el request no llegó, o el servidor
//     se cayó— y no se reintenta cuando el servidor YA CONTESTÓ QUE NO.
//
//   • **No se toca `lib/queryClient.ts`.** Es la política de reintentos de toda
//     la app; un panel no la cambia. Todo esto vive por consulta.
//
//   • **No entra `placeholderData: keepPreviousData`** por la puerta de atrás.
//     Está rechazado a propósito: pintaría las cifras del rango anterior bajo el
//     rótulo del rango nuevo, que es la familia de defectos que este PR viene
//     arreglando tres veces. Blanquear sigue siendo el lado seguro.
//
// El único 4xx que en general SÍ es transitorio es el 429, y tampoco entra: en
// este backend lo emite únicamente `authController.js:588` para intentos de
// login, no hay límite de tasa sobre `/yardMetrics`, y reintentar un 429 sin
// honrar `Retry-After` es empujar contra la puerta que acaba de decir "esperá".
// Si algún día se pone un rate limit al endpoint, éste es el lugar donde
// agregarlo — con el `Retry-After`, no sin él.

/**
 * ¿El fallo dice "no se supo" en vez de "no"?
 *
 * Transitorio = sin respuesta del servidor (DNS, wifi caído, el `ECONNABORTED`
 * del `timeout: 30000` de utils/api.ts) o 5xx. Todo lo demás no lo es:
 *
 *  - una CANCELACIÓN no es un fallo: `utils/api.ts` rechaza con `axios.Cancel`
 *    en logout y con el token vencido, y `setAdminAbortSignal` aborta los GET en
 *    vuelo al cambiar de empresa. Reintentar ahí re-dispara el request que
 *    alguien acaba de mandar a matar;
 *  - un error que no es de axios es un defecto del cliente (p. ej. leer el
 *    cuerpo de una respuesta con otra forma), y la segunda vuelta falla igual.
 */
export function isTransientYardMetricsError(error: unknown): boolean {
  if (axios.isCancel(error)) return false;
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === undefined) return true;
  return status >= 500;
}

/**
 * Dos reintentos, o sea tres intentos en total.
 *
 * Con el `retryDelay` por defecto de TanStack —`min(1000 * 2^n, 30000)`, o sea
 * 1 s y 2 s— un corte de red de un par de segundos se absorbe solo y el usuario
 * no ve más que el "Cargando…" que ya estaba: mientras reintenta, la consulta
 * sigue en `isPending` y el panel no llega a pintar el error.
 *
 * El techo importa porque el peor caso se multiplica: la tira de tarjetas pide
 * hasta 10 patios a la vez. Por eso el panel (N = 1, el patio abierto) es el
 * único que reintenta, y `useYardMetrics` se queda en `retry: 0` — la tarjeta
 * ya se cura sola con su `refetchInterval` de 30 s, y el panel sobre un rango
 * pasado no tiene ese intervalo. Ese es el hueco, y no hay otro.
 *
 * `failureCount` llega en 0 en el primer fallo (query-core/retryer.js:89 lo
 * incrementa DESPUÉS de consultar este predicado), así que `< 2` son dos
 * reintentos y no uno.
 */
export const YARD_METRICS_MAX_RETRIES = 2;

export function shouldRetryYardMetrics(
  failureCount: number,
  error: unknown,
): boolean {
  return (
    failureCount < YARD_METRICS_MAX_RETRIES && isTransientYardMetricsError(error)
  );
}
